import React, { useState, useEffect, useRef } from 'react';
import OnboardingFailurePage from '../../pages/OnboardingFailurePage';
import { createPortal } from 'react-dom';
import { X, ArrowLeft, ArrowRight, Building2, Users, CreditCard, CheckCircle, Mail, AlertCircle } from 'lucide-react';
import { useUser } from '@clerk/clerk-react';
import { useClerk } from '@clerk/clerk-react';
import { api } from '../../lib/api';
import UserInfoForm from './UserInfoForm';
import OrganizationInfoForm from './OrganizationInfoForm';
import BillingInfoForm from './BillingInfoForm';
import JoinOrganizationForm from './JoinOrganizationForm';
import { showAlert } from '../common/AppDialogs';

interface OnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  initialStep?: OnboardingStep;
  isAdditionalOrganization?: boolean;
  directPath?: OnboardingPath; // NEW: Skip choice screen and go directly to create or join
}

type OnboardingStep = 'choice' | 'user_info' | 'org_info' | 'billing' | 'join_org' | 'join_request_submitted';
type OnboardingPath = 'create_org' | 'join_org';

interface OnboardingData {
  path?: OnboardingPath;
  userInfo?: {
    firstName: string;
    lastName: string;
    preferredName?: string;
    email: string;
    jobTitle: string;
  };
  orgInfo?: {
    companyName: string;
    companyLogo?: File;
    companyWebsite?: string;
    companyPhone?: string;
    companyABN?: string;
    companyACN?: string;
  };
  billingInfo?: {
    signupCode?: string;
    stripePaymentMethodId?: string;
  };
  joinInfo?: {
    joiningCode: string;
    firstName: string;
    lastName: string;
    preferredName?: string;
    email: string;
    jobTitle?: string;
    message?: string;
  };
}

export default function OnboardingModal({ isOpen, onClose, onComplete, initialStep = 'choice', isAdditionalOrganization = false, directPath }: OnboardingModalProps) {
  // Determine initial step and path based on props
  let effectiveInitialStep: OnboardingStep;
  let initialPath: OnboardingPath | undefined;
  
  if (directPath) {
    // If directPath is provided, skip choice screen and go directly to the appropriate form
    effectiveInitialStep = directPath === 'create_org' ? 'user_info' : 'join_org';
    initialPath = directPath;
  } else {
    // Normal flow
    effectiveInitialStep = initialStep;
  }
  
  const [currentStep, setCurrentStep] = useState<OnboardingStep>(effectiveInitialStep);
  const [onboardingData, setOnboardingData] = useState<OnboardingData>({ path: initialPath });
  
  // CRITICAL FIX: Calculate the effective step dynamically instead of relying on state
  const getEffectiveStep = (): OnboardingStep => {
    // If we have a directPath and we're still on the initial step, use directPath logic
    if (isOpen && directPath && currentStep === effectiveInitialStep) {
      return directPath === 'create_org' ? 'user_info' : 'join_org';
    }
    // Otherwise, use the current step state (which may have been updated by navigation)
    return currentStep;
  };
  
  const getEffectiveOnboardingData = (): OnboardingData => {
    if (isOpen && directPath) {
      // Merge the directPath with existing onboardingData to preserve accumulated form data
      return { ...onboardingData, path: directPath };
    }
    return onboardingData;
  };
  
  const effectiveStep = getEffectiveStep();
  const effectiveOnboardingData = getEffectiveOnboardingData();
  
  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(initialStep);
      setOnboardingData({});
      setShowCloseConfirmation(false);
      setIsLoading(false);
      setOnboardingCompleted(false);
      setWaitingForOrganizationSetup(false);
      setShowFailurePage(false);
    }
  }, [isOpen, initialStep]);
  const [isLoading, setIsLoading] = useState(false);
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);
  const [waitingForOrganizationSetup, setWaitingForOrganizationSetup] = useState(false);
  const [showFailurePage, setShowFailurePage] = useState(false);
  const [failureData, setFailureData] = useState<{
    companyName?: string;
    userEmail?: string;
    discountCode?: string;
    timestamp?: string;
  }>({});
  const { user } = useUser();
  const clerk = useClerk();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup function - delete user from both Nexus and Clerk
  const handleCleanupAndSignOut = async () => {
    try {
      // If this is for an additional organization, just close the modal
      if (isAdditionalOrganization) {
        onClose();
        return;
      }
      
      // CRITICAL: Only cleanup users who are actually in onboarding process
      // Don't cleanup existing users who shouldn't be seeing this modal
      if (effectiveStep !== 'join_request_submitted') {
        // Call cleanup API
        await api.delete('/users/cleanup-incomplete');
      } else {
      }
      
      // Sign out from Clerk
      await clerk.signOut();
      
      // Redirect to homepage
      window.location.href = '/';
    } catch (error) {
      console.error('❌ Cleanup failed:', error);
      
      // Still try to sign out even if cleanup fails
      try {
        await clerk.signOut();
        window.location.href = '/';
      } catch (signOutError) {
        console.error('❌ Sign out failed:', signOutError);
      }
    }
  };

  // Check organization setup after a brief delay
  const checkForOrganizationSetup = async () => {
    try {
      
      const response = await api.get('/onboarding/status');
      
      
      if (response.data.data?.organizationId) {
        setWaitingForOrganizationSetup(false);
        setOnboardingCompleted(true);
        onComplete();
        return;
      }
      
      // If no organization found after delay, show failure page
      console.error('❌ Organization not found after setup delay');
      setWaitingForOrganizationSetup(false);
      setIsLoading(false);
      
      // Set failure data for the support email
      setFailureData({
        companyName: onboardingData.orgInfo?.companyName,
        userEmail: user?.emailAddresses?.[0]?.emailAddress,
        discountCode: onboardingData.billingInfo?.signupCode,
        timestamp: new Date().toISOString()
      });
      setShowFailurePage(true);
      
    } catch (error) {
      console.error('❌ Error checking for organization setup:', error);
      setWaitingForOrganizationSetup(false);
      setIsLoading(false);
      
      // Set failure data for the support email
      setFailureData({
        companyName: onboardingData.orgInfo?.companyName,
        userEmail: user?.emailAddresses?.[0]?.emailAddress,
        discountCode: onboardingData.billingInfo?.signupCode,
        timestamp: new Date().toISOString()
      });
      setShowFailurePage(true);
    }
  };

  // Retry function for failure page
  const handleRetryPolling = () => {
    setShowFailurePage(false);
    setWaitingForOrganizationSetup(true);
    // Start check after 2-second delay
    pollingRef.current = setTimeout(checkForOrganizationSetup, 2000);
  };

  // Reset activity timer
  const resetActivityTimer = () => {
    lastActivityRef.current = Date.now();
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Set 10-minute timeout for inactivity (only if not completed)
    timeoutRef.current = setTimeout(() => {
      if (!onboardingCompleted) {
        handleCleanupAndSignOut();
      }
    }, 10 * 60 * 1000); // 10 minutes
  };

  // Reset state when modal opens and start activity timer
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(effectiveInitialStep);
      setOnboardingData({ path: initialPath });
      setIsLoading(false);
      setOnboardingCompleted(false);
      setWaitingForOrganizationSetup(false);
      resetActivityTimer();
    } else {
      // Clear timeouts when modal closes
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
        pollingRef.current = null;
      }
    }
    
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
      }
    };
  }, [isOpen, effectiveInitialStep, initialPath]);

  // Track user activity to reset timer
  useEffect(() => {
    const handleActivity = () => {
      if (isOpen) {
        resetActivityTimer();
      }
    };

    if (isOpen) {
      // Add activity listeners
      document.addEventListener('mousedown', handleActivity);
      document.addEventListener('keydown', handleActivity);
      document.addEventListener('scroll', handleActivity);
      
      return () => {
        document.removeEventListener('mousedown', handleActivity);
        document.removeEventListener('keydown', handleActivity);
        document.removeEventListener('scroll', handleActivity);
      };
    }
  }, [isOpen]);

  const handlePathChoice = (path: OnboardingPath) => {
    setOnboardingData({ ...onboardingData, path });
    if (path === 'create_org') {
      setCurrentStep('user_info');
    } else {
      setCurrentStep('join_org');
    }
  };

  const handleStepComplete = (stepData: any) => {
    
    const updatedData = { ...effectiveOnboardingData, ...stepData };
    setOnboardingData(updatedData);

    // Navigate to next step based on effective step and path
    if (effectiveStep === 'user_info' && updatedData.path === 'create_org') {
      setCurrentStep('org_info');
    } else if (effectiveStep === 'org_info') {
      setCurrentStep('billing');
    } else if (effectiveStep === 'billing') {
      // Final step for create org - complete onboarding
      handleCompleteOnboarding(updatedData);
    } else if (effectiveStep === 'join_org') {
      // Submit join request and show confirmation
      handleSubmitJoinRequest(updatedData);
    }
  };

  const handleSubmitJoinRequest = async (data: OnboardingData) => {
    setIsLoading(true);
    
    try {
      // Submit join request
      
      const response = await api.post('/organizations/onboard/join-request', data.joinInfo);
      
      // For additional organizations, if immediately approved, switch to the organization
      if (directPath && response.data.data?.organizationId) {
        await handleSwitchToNewOrganization(response.data.data.organizationId, response.data.data.organizationName);
        return;
      }
      
      // Move to confirmation step
      setCurrentStep('join_request_submitted');
      setIsLoading(false);
    } catch (error: any) {
      console.error('Join request error:', error);
      
      if (error.response?.data?.data?.alreadyMember) {
        // Handle case where user is already a member
        showAlert('You are already a member of this organization!');
        setOnboardingCompleted(true);
        onComplete();
        return;
      }
      
      if (error.response?.data?.code === 'ALREADY_MEMBER') {
        showAlert('You are already a member of an organization. Users can only be part of one organization at a time. Please contact support if you need to switch organizations.');
        setOnboardingCompleted(true);
        onComplete();
        return;
      }
      
      showAlert(error.response?.data?.error || error.message || 'An error occurred while submitting your request');
      setIsLoading(false);
    }
  };

  const handleSwitchToNewOrganization = async (organizationId: string, organizationName?: string) => {
    try {
      
      // Show success message
      setCurrentStep('join_request_submitted'); // Reuse this step for success display
      setIsLoading(false);
      
      // Call organization switch API
      await api.put('/users/active-organization', { organizationId });
      
      
      // Close modal and trigger page refresh after a brief delay
      setTimeout(() => {
        onComplete();
        // Force page refresh to ensure all data is reloaded for the new organization
        window.location.href = '/';
      }, 2000);
      
    } catch (error) {
      console.error('❌ Failed to switch to new organization:', error);
      showAlert('Organization created successfully, but failed to switch to it. Please refresh the page and select the new organization from the header.');
      onComplete();
    }
  };

  const handleCompleteOnboarding = async (data: OnboardingData) => {
    setIsLoading(true);
    
    try {
      if (data.path === 'create_org') {
        // Create organization
        
        // Safety check for required data
        if (!data.orgInfo) {
          console.error('❌ Missing orgInfo data:', data);
          throw new Error('Organization information is missing. Please go back and fill out the organization details.');
        }
        
        if (!data.userInfo) {
          console.error('❌ Missing userInfo data:', data);
          throw new Error('User information is missing. Please go back and fill out your details.');
        }
        
        if (!data.billingInfo) {
          console.error('❌ Missing billingInfo data:', data);
          throw new Error('Billing information is missing. Please go back and fill out the billing details.');
        }
        
        // Convert logo file to base64 if present
        let processedData = { ...data };
        if (data.orgInfo.companyLogo && data.orgInfo.companyLogo instanceof File) {
          try {
            const logoBase64 = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result as string);
              reader.onerror = reject;
              reader.readAsDataURL(data.orgInfo.companyLogo);
            });
            
            processedData = {
              ...data,
              orgInfo: {
                ...data.orgInfo,
                companyLogo: {
                  fileName: data.orgInfo.companyLogo.name,
                  mimeType: data.orgInfo.companyLogo.type,
                  base64Data: logoBase64
                }
              }
            } as any;
          } catch (error) {
            console.error('Failed to process logo file:', error);
            // Continue without logo if processing fails
            processedData = {
              ...data,
              orgInfo: {
                ...data.orgInfo,
                companyLogo: undefined
              }
            };
          }
        }

        const response = await api.post('/organizations/onboard/create-organization', processedData);

        // Success - response.data contains the result
        
        const createdOrganizationId = response.data.data?.organizationId ||
          response.data.data?.organization?.organizationId ||
          response.data.data?.organization?.id;

        // For additional organizations, switch to the new organization
        if (directPath && createdOrganizationId) {
          await handleSwitchToNewOrganization(createdOrganizationId, data.orgInfo?.companyName);
          return;
        }
        
        // Fallback for additional organizations without organizationId or if switch fails
        if (directPath) {
          setIsLoading(false);
          setCurrentStep('join_request_submitted');
          setTimeout(() => {
            onComplete();
          }, 2000);
          return;
        }
        
      }
      
      // Start organization setup check after 2-second delay (only for initial onboarding)
      if (!directPath) {
        setIsLoading(false);
        setWaitingForOrganizationSetup(true);
        pollingRef.current = setTimeout(checkForOrganizationSetup, 2000);
      }
    } catch (error: any) {
      console.error('Onboarding error:', error);
      
      // Handle specific error cases
      if (error.response?.data?.code === 'ALREADY_MEMBER') {
        showAlert('You are already a member of an organization. Users can only be part of one organization at a time. Please contact support if you need to switch organizations.');
        setOnboardingCompleted(true);
      onComplete();
        return;
      }
      
      showAlert(error.response?.data?.error || error.message || 'An error occurred during onboarding');
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (effectiveStep === 'user_info' || effectiveStep === 'join_org') {
      setCurrentStep('choice');
    } else if (effectiveStep === 'org_info') {
      setCurrentStep('user_info');
    } else if (effectiveStep === 'billing') {
      setCurrentStep('org_info');
    }
    // No back button for join_request_submitted step
  };

  const getStepTitle = () => {
    switch (effectiveStep) {
      case 'choice':
        return 'Welcome to Nexus';
      case 'user_info':
        return 'Your Information';
      case 'org_info':
        return 'Organization Details';
      case 'billing':
        return 'Billing Information';
      case 'join_org':
        return 'Join Organization';
      case 'join_request_submitted':
        return 'Request Submitted';
      default:
        return 'Welcome to Nexus';
    }
  };

  const getStepIcon = () => {
    switch (effectiveStep) {
      case 'user_info':
        return <Users className="h-5 w-5" />;
      case 'org_info':
        return <Building2 className="h-5 w-5" />;
      case 'billing':
        return <CreditCard className="h-5 w-5" />;
      case 'join_org':
        return <Users className="h-5 w-5" />;
      case 'join_request_submitted':
        return <CheckCircle className="h-5 w-5" />;
      default:
        return null;
    }
  };

  const renderStepContent = () => {
    switch (effectiveStep) {
      case 'choice':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="h-16 w-16 rounded-lg bg-primary flex items-center justify-center mx-auto mb-4">
                <span className="text-primary-foreground font-bold text-2xl">N</span>
              </div>
              <p className="text-lg text-muted-foreground">
                How would you like to get started?
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={() => handlePathChoice('create_org')}
                className="p-6 border-2 border-input rounded-lg hover:border-primary hover:bg-accent transition-colors text-left"
              >
                <Building2 className="h-8 w-8 text-primary mb-3" />
                <h3 className="text-lg font-semibold mb-2">Create a new organization</h3>
                <p className="text-sm text-muted-foreground">
                  Set up a new organization and become the administrator
                </p>
              </button>

              <button
                onClick={() => handlePathChoice('join_org')}
                className="p-6 border-2 border-input rounded-lg hover:border-primary hover:bg-accent transition-colors text-left"
              >
                <Users className="h-8 w-8 text-primary mb-3" />
                <h3 className="text-lg font-semibold mb-2">Join an existing organization</h3>
                <p className="text-sm text-muted-foreground">
                  Enter a joining code to request access to an organization
                </p>
              </button>
            </div>
          </div>
        );

      case 'user_info':
        return (
          <UserInfoForm
            initialData={effectiveOnboardingData.userInfo}
            userEmail={user?.emailAddresses[0]?.emailAddress || ''}
            userFirstName={user?.firstName || ''}
            userLastName={user?.lastName || ''}
            onSubmit={(data) => handleStepComplete({ userInfo: data })}
            isLoading={isLoading}
          />
        );

      case 'org_info':
        return (
          <OrganizationInfoForm
            initialData={effectiveOnboardingData.orgInfo}
            onSubmit={(data) => handleStepComplete({ orgInfo: data })}
            isLoading={isLoading}
          />
        );

      case 'billing':
        return (
          <BillingInfoForm
            initialData={effectiveOnboardingData.billingInfo}
            onSubmit={(data) => handleStepComplete({ billingInfo: data })}
            isLoading={isLoading}
          />
        );

      case 'join_org':
        return (
          <JoinOrganizationForm
            userEmail={user?.emailAddresses[0]?.emailAddress || ''}
            userFirstName={user?.firstName || ''}
            userLastName={user?.lastName || ''}
            onSubmit={(data) => handleStepComplete({ joinInfo: data })}
            isLoading={isLoading}
          />
        );

      case 'join_request_submitted':
        return (
          <div className="space-y-6 text-center">
            <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            
            <div>
              {directPath ? (
                <>
                  <h3 className="text-xl font-semibold mb-2">Successfully joined organization!</h3>
                  <p className="text-muted-foreground">
                    Switching to your new organization...
                  </p>
                </>
              ) : (
                <>
                  <h3 className="text-xl font-semibold mb-2">Request Submitted Successfully!</h3>
                  <p className="text-muted-foreground">
                    Your request to join the organization has been sent to the administrators.
                  </p>
                </>
              )}
            </div>

            {!directPath && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div className="text-left">
                    <h4 className="font-medium text-blue-900 mb-2">What happens next?</h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• Organization administrators will review your request</li>
                      <li>• You'll receive an email notification with their decision</li>
                      <li>• If approved, you'll gain access to the organization's workspace</li>
                      <li>• This process typically takes 1-2 business days</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {!directPath && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h4 className="font-medium text-amber-900 mb-2">Made an error?</h4>
                <div className="flex gap-2 text-sm">
                  <button
                    onClick={() => {
                      setCurrentStep('choice');
                      setOnboardingData({ path: 'create_org' });
                    }}
                    className="text-amber-800 underline hover:text-amber-900"
                  >
                    Create your own organisation
                  </button>
                  <span className="text-amber-700">or</span>
                  <button
                    onClick={() => {
                      setCurrentStep('join_org');
                      setOnboardingData({ path: 'join_org' });
                    }}
                    className="text-amber-800 underline hover:text-amber-900"
                  >
                    request to join a different organisation
                  </button>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  // Show failure page if polling failed
  if (showFailurePage) {
    return createPortal(
      <OnboardingFailurePage 
        organizationData={failureData}
        onRetry={handleRetryPolling}
      />,
      document.body
    );
  }

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            {getStepIcon()}
            <h2 className="text-xl font-semibold">{getStepTitle()}</h2>
          </div>
          <button
            onClick={() => {
              // Only show confirmation for steps that would trigger cleanup
              if (effectiveStep === 'join_request_submitted') {
                // For pending requests, just sign out directly (no cleanup)
                handleCleanupAndSignOut();
              } else {
                // For active onboarding steps, show confirmation
                setShowCloseConfirmation(true);
              }
            }}
            className="text-muted-foreground hover:text-destructive transition-colors"
            disabled={isLoading}
            title={effectiveStep === 'join_request_submitted' ? "Sign out" : "Cancel onboarding and sign out"}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Progress indicator for multi-step flows */}
        {effectiveOnboardingData.path === 'create_org' && effectiveStep !== 'choice' && (
          <div className="px-6 py-4 border-b bg-muted/30">
            <div className="flex items-center justify-between text-sm">
              <span className={effectiveStep === 'user_info' ? 'text-primary font-medium' : 'text-muted-foreground'}>
                1. Your Information
              </span>
              <span className={effectiveStep === 'org_info' ? 'text-primary font-medium' : 'text-muted-foreground'}>
                2. Organization Details
              </span>
              <span className={effectiveStep === 'billing' ? 'text-primary font-medium' : 'text-muted-foreground'}>
                3. Billing Setup
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-1 mt-2">
              <div 
                className="bg-primary h-1 rounded-full transition-all duration-300"
                style={{ 
                  width: effectiveStep === 'user_info' ? '33%' : 
                         effectiveStep === 'org_info' ? '66%' : 
                         effectiveStep === 'billing' ? '100%' : '0%' 
                }}
              />
            </div>
          </div>
        )}

        {/* Content */}
      <div className="p-6">
        {(() => {
          return waitingForOrganizationSetup ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <h3 className="text-lg font-semibold mb-2">Setting up your organization...</h3>
                <p className="text-muted-foreground">
                  We're preparing your workspace and setting up your account. This will just take a moment.
                </p>
              </div>
            ) : (
              renderStepContent()
            );
          })()}
        </div>

        {/* Footer with navigation */}
        {effectiveStep !== 'choice' && effectiveStep !== 'join_request_submitted' && !waitingForOrganizationSetup && (
          <div className="flex justify-between items-center p-6 border-t bg-muted/30">
            <button
              onClick={handleBack}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 border border-input rounded-md hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>

            <div className="text-sm text-muted-foreground">
              {isLoading && 'Processing...'}
            </div>
          </div>
        )}
      </div>

      {/* Close Confirmation Modal */}
      {showCloseConfirmation && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <div className="bg-background rounded-lg shadow-lg w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <AlertCircle className="h-6 w-6 text-amber-500" />
                <h3 className="text-lg font-semibold">Cancel Sign-up?</h3>
              </div>
              
              <p className="text-muted-foreground mb-6">
                Clicking this button cancels your sign-up, and next time you return, you will need to click 'Get Started' again. 
                If you would rather return later and choose one of these options, click 'Don't close' and then close the browser window directly.
              </p>
              
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowCloseConfirmation(false)}
                  className="px-4 py-2 border border-border rounded-md hover:bg-muted transition-colors"
                >
                  Don't close
                </button>
                <button
                  onClick={handleCleanupAndSignOut}
                  className="px-4 py-2 bg-destructive text-destructive-foreground rounded-md hover:bg-destructive/90 transition-colors"
                >
                  Close and cancel sign-up
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
